import { getSidebarItem } from "../../../config/sidebarConfig";
import type { AuthUser } from "../../../types/authUser";
import { getFeatureFlagsFromUser, isFeatureFlagEnabled } from "../../../utils/featureFlags";
import { canOpenSidebarItem } from "../../navigation/sidebarAccess";
import { accountEntries } from "./account";
import { adCenterEntries } from "./adCenter";
import { calendarEntries } from "./calendar";
import { certificatesEntries } from "./certificates";
import { circlesEntries } from "./circles";
import {
  conceptsCirclesEntries,
  conceptsCreateEntries,
  conceptsDriveEntries,
  conceptsHelpCenterEntries,
  conceptsHomeEntries,
  conceptsNoticesEntries,
  conceptsUsersEntries,
} from "./concepts";
import { createEntries } from "./create";
import { driveEntries } from "./drive";
import { helpCenterEntries } from "./helpCenter";
import { homeEntries } from "./home";
import { libraryEntries } from "./library";
import { noticesEntries } from "./notices";
import { teamEntries } from "./team";
import {
  troubleshootingCalendarEntries,
  troubleshootingDriveEntries,
  troubleshootingNoticesEntries,
  troubleshootingUsersEntries,
} from "./troubleshooting";
import { unsupportedCreateEntries } from "./unsupported";
import { usersEntries } from "./users";
import type { CatalogueEntry } from "./types";

export { isSlotQuestion } from "./types";
export type {
  AnswerContext,
  AnswerEntry,
  AnswerLink,
  AnswerValues,
  CatalogueEntry,
  FilledSlots,
  HandoffEntry,
  HandoffStart,
  HandoffTarget,
  HowToAction,
  HowToEntry,
  SlotName,
  SlotQuestion,
  SlotValues,
  SummaryLine,
} from "./types";

/** Every question Ask Noticeboard can answer, one module per sidebar item (Certificates beside Create, where it opens), in sidebar order. */
export const CATALOGUE: CatalogueEntry[] = [
  ...homeEntries,
  ...conceptsHomeEntries,
  ...createEntries,
  ...conceptsCreateEntries,
  ...unsupportedCreateEntries,
  ...certificatesEntries,
  ...noticesEntries,
  ...conceptsNoticesEntries,
  ...troubleshootingNoticesEntries,
  ...calendarEntries,
  ...troubleshootingCalendarEntries,
  ...usersEntries,
  ...conceptsUsersEntries,
  ...troubleshootingUsersEntries,
  ...circlesEntries,
  ...conceptsCirclesEntries,
  ...teamEntries,
  ...libraryEntries,
  ...driveEntries,
  ...conceptsDriveEntries,
  ...troubleshootingDriveEntries,
  ...helpCenterEntries,
  ...conceptsHelpCenterEntries,
  ...adCenterEntries,
  ...accountEntries,
];

/** The entries this person may ask about: those whose sidebar items they can all open, with their feature on and their page showing them. */
export function getAvailableEntries(
  user: AuthUser | null | undefined
): CatalogueEntry[] {
  const flags = getFeatureFlagsFromUser(user);
  return CATALOGUE.filter(
    (entry) =>
      [entry.sidebarItem, ...(entry.alsoNeeds ?? [])].every((key) =>
        canOpenSidebarItem(getSidebarItem(key), user)
      ) &&
      (!entry.featureFlag || isFeatureFlagEnabled(flags, entry.featureFlag)) &&
      (!entry.visibleTo || entry.visibleTo(user))
  );
}
